<script setup lang="ts">
import { useCorporativeAccount } from '@/store/corporative'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import useStore from '@/store/auth'
import AccountMenu from './AccountMenu.vue'
import emptyStateNoIconCorporate from '@/assets/icons/white/business.svg'
import eventIconGreen from '@/assets/icons/green/home.svg'
import eventIconGrey from '@/assets/icons/grey/home.svg'
import contactIconGreen from '@/assets/icons/green/people.svg'
import contactIconGrey from '@/assets/icons/grey/people.svg'
import memberIconGreen from '@/assets/icons/green/members.svg'
import memberIconGrey from '@/assets/icons/grey/members.svg'
import settingsGrey from '@/assets/icons/grey/settings.svg'
import settingsGreen from '@/assets/icons/green/settings.svg'
import { useRoute, type RouteLocationRaw } from 'vue-router'
import {
  computed,
  nextTick,
  ref,
  watch,
  onMounted,
  onUnmounted,
  watchEffect,
} from 'vue'
import { useDisplay } from 'vuetify'
import { useLayout } from '@/composables/useLayout'
import type { ComponentInternalInstance } from 'vue'
import { useNotifications } from '@/modules/expert/notifications/store'

type MenuId = 'notifications' | 'events' | 'contacts' | 'members'

type MenuItem = {
  id: MenuId
  text: string
  iconGreen?: string
  iconGrey?: string
  to: RouteLocationRaw
  useMdi?: boolean
  badge?: number
}

const notificationsStore = useNotifications()
const { notifications } = storeToRefs(notificationsStore)

const accountCorporativeStore = useCorporativeAccount()
const { isUsingCorporateAccount, selectedAccount, shouldUseEmptyStateLogo } =
  storeToRefs(accountCorporativeStore)

const authStore = useStore()
const { user } = storeToRefs(authStore)

const { rail, isSidebarOpen } = useLayout()
const { t } = useI18n()
const route = useRoute()
const { mobile } = useDisplay()

const accountMenu = ref(false)
const isMobile = computed(() => mobile.value)

const isRailCollapsed = computed(() => !isMobile.value && rail.value)

const notificationsCount = computed(() => {
  return notifications.value.filter((item) => !item.read).length
})

const drawer = computed<boolean>({
  get: () => (mobile.value ? isSidebarOpen.value : true),
  set: (val) => {
    if (mobile.value) isSidebarOpen.value = val
  },
})

function normalizeId(
  value: string | string[] | number | null | undefined
): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : null
  }

  if (Array.isArray(value) && typeof value[0] === 'string') {
    const trimmed = value[0].trim()
    return trimmed.length ? trimmed : null
  }

  if (typeof value === 'number') return String(value)

  return null
}

const companyId = computed<string | null>(() =>
  normalizeId(route.params.id ?? selectedAccount.value?.id)
)

const menus = computed<MenuItem[]>(() => [
  {
    id: 'notifications',
    text: t('notification.title'),
    useMdi: true,
    badge: notificationsCount.value,
    to: companyId.value
      ? { name: 'corporative-notifications', params: { id: companyId.value } }
      : {},
  },
  {
    id: 'events',
    text: t('general.events'),
    iconGreen: eventIconGreen,
    iconGrey: eventIconGrey,
    to: companyId.value
      ? { name: 'events-list', params: { id: companyId.value } }
      : {},
  },
  {
    id: 'contacts',
    text: t('general.contacts'),
    iconGreen: contactIconGreen,
    iconGrey: contactIconGrey,
    to: companyId.value
      ? { name: 'personal-contacts-list', params: { id: companyId.value } }
      : {},
  },
  {
    id: 'members',
    text: t('general.members'),
    iconGreen: memberIconGreen,
    iconGrey: memberIconGrey,
    to: companyId.value
      ? { name: 'members-list', params: { id: companyId.value } }
      : {},
  },
])

function getRouteName(): string {
  return typeof route.name === 'string' ? route.name : ''
}

function isPathInExpertSection(section: string): boolean {
  const path = route.path || ''
  const regex = new RegExp(`/expert/[^/]+/${section}(?:/|$)`, 'i')
  return regex.test(path)
}

function isContactsSettingsPath(): boolean {
  const path = route.path || ''
  return (
    /\/expert\/[^/]+\/settings\/custom-fields(?:\/|$)/i.test(path) ||
    /\/expert\/[^/]+\/settings\/custom-field-groups(?:\/|$)/i.test(path) ||
    /\/expert\/[^/]+\/settings\/required-fields(?:\/|$)/i.test(path) ||
    /\/expert\/[^/]+\/settings\/privacy(?:\/|$)/i.test(path)
  )
}

function isMembersTabsPath(): boolean {
  const path = route.path || ''
  return /\/expert\/[^/]+\/(members|roles|invites)(?:\/|$)/i.test(path)
}

function isMembersByName(): boolean {
  const name = getRouteName()
  return (
    name === 'members-list' ||
    name.startsWith('members-') ||
    name.startsWith('roles-') ||
    name.startsWith('invites-')
  )
}

function isEventsByName(): boolean {
  const name = getRouteName()
  return name === 'events-list' || name.startsWith('events-')
}

function isContactsByName(): boolean {
  const name = getRouteName()
  return (
    name === 'personal-contacts-list' ||
    name.startsWith('personal-contacts-') ||
    name.startsWith('contacts-')
  )
}

function isNotificationsByName(): boolean {
  const name = getRouteName()
  return (
    name === 'corporative-notifications' || name.startsWith('notifications-')
  )
}

function isMenuActive(id: MenuId): boolean {
  switch (id) {
    case 'notifications':
      return isNotificationsByName()

    case 'events':
      return isEventsByName() || isPathInExpertSection('events')

    case 'contacts':
      return (
        isContactsByName() ||
        isPathInExpertSection('contacts') ||
        isContactsSettingsPath()
      )

    case 'members':
      return isMembersByName() || isMembersTabsPath()

    default:
      return false
  }
}

function isSettingsActive(): boolean {
  const name = getRouteName()
  return (
    name === 'detail-corporative-account' ||
    name.startsWith('corporative-account-')
  )
}

function updateComboboxDropdownPosition(): void {
  nextTick(() => {
    const overlayEl = document.querySelector('.v-overlay.v-overlay--active')

    if (!overlayEl) return

    const instance = (
      overlayEl as { __vueParentComponent?: ComponentInternalInstance }
    ).__vueParentComponent as ComponentInternalInstance | null

    const ctx = instance?.proxy as { updateLocation?: () => void } | null
    ctx?.updateLocation?.()
  })
}

function formatBadgeContent(value?: number) {
  const count = Number(value ?? 0)

  if (!Number.isFinite(count) || count <= 0) return ''
  if (count > 99) return '99+'

  return String(count)
}

watch(rail, () => {
  updateComboboxDropdownPosition()
})

watch(accountMenu, (val) => {
  if (!val) rail.value = true
})

watch(drawer, (isOpen) => {
  if (!isMobile.value) return
  document.body.classList.toggle('no-scroll', !!isOpen)
})

watch(isMobile, (now) => {
  if (!now) {
    document.body.classList.remove('no-scroll')
  } else {
    document.body.classList.toggle('no-scroll', !!drawer.value)
  }
})

let notificationsInterval: ReturnType<typeof setInterval> | null = null

async function loadNotifications() {
  if (!companyId.value) return
  await notificationsStore.getNotifications(companyId.value)
}

function startNotificationsPolling() {
  stopNotificationsPolling()

  notificationsInterval = setInterval(
    async () => {
      if (document.hidden) return
      await loadNotifications()
    },
    1000 * 60 * 3
  )
}

function stopNotificationsPolling() {
  if (!notificationsInterval) return

  clearInterval(notificationsInterval)
  notificationsInterval = null
}

async function handleVisibilityChange() {
  if (!document.hidden) {
    await loadNotifications()
  }
}

watch(companyId, async (newCompanyId, oldCompanyId) => {
  if (!newCompanyId) return

  if (newCompanyId !== oldCompanyId) {
    await loadNotifications()
    startNotificationsPolling()
  }
})

const sidebarWidth = computed(() => {
  if (isMobile.value) return 0
  return rail.value ? 72 : 300
})

watchEffect(() => {
  document.documentElement.style.setProperty(
    '--expert-sidebar-width',
    `${sidebarWidth.value}px`
  )
})

onMounted(async () => {
  await loadNotifications()
  startNotificationsPolling()
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  stopNotificationsPolling()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  document.body.classList.remove('no-scroll')
})
</script>

<template>
  <div
    v-if="isMobile && drawer"
    class="sidebar-overlay"
    @click="drawer = false"
  />

  <v-navigation-drawer
    v-model="drawer"
    :rail="!isMobile && rail"
    :width="isMobile ? 255 : 300"
    height="100vh"
    :absolute="isMobile"
    :permanent="!isMobile"
    nav
    class="position-fixed transition-all duration-300"
  >
    <v-list ref="comboboxRef">
      <v-list-item
        v-if="!isMobile"
        class="w-100 bottom-border text-nowrap"
        nav
        :style="
          isUsingCorporateAccount && shouldUseEmptyStateLogo
            ? 'background-color: var(--v-theme-green);'
            : ''
        "
      >
        <div class="d-flex flex-column w-100">
          <v-list-item-title class="text-body-1 font-weight-medium mt-1">
            {{
              isUsingCorporateAccount
                ? selectedAccount?.tradingName
                : user?.name
            }}
          </v-list-item-title>

          <v-list-item-subtitle class="text-caption">
            {{
              isUsingCorporateAccount
                ? t('header.corporateAccount')
                : t('header.personalAccount')
            }}
          </v-list-item-subtitle>
        </div>

        <template #prepend>
          <img
            :src="
              isUsingCorporateAccount
                ? shouldUseEmptyStateLogo
                  ? emptyStateNoIconCorporate
                  : String(selectedAccount?.logo)
                : user?.profilePicture
            "
            alt=""
            class="ml-1 mr-4"
            :class="[isUsingCorporateAccount ? 'rounded' : 'rounded-xl']"
            width="32"
            :style="
              isUsingCorporateAccount && shouldUseEmptyStateLogo
                ? 'padding: 8px; background-color: #8DC63F;'
                : ''
            "
          />
        </template>

        <template #append>
          <AccountMenu @update:model-value="(val) => (accountMenu = val)" />
        </template>
      </v-list-item>

      <v-list-group
        v-else
        :value="
          isUsingCorporateAccount ? selectedAccount?.tradingName : user?.name
        "
      >
        <template #activator="{ props: mobileProps }">
          <v-list-item v-bind="mobileProps" class="w-100 mt-0 text-nowrap">
            <div class="d-flex flex-column">
              <v-list-item-title class="text-body-1 font-weight-medium mt-1">
                {{
                  isUsingCorporateAccount
                    ? selectedAccount?.tradingName
                    : user?.name
                }}
              </v-list-item-title>

              <v-list-item-subtitle class="text-caption">
                {{
                  isUsingCorporateAccount
                    ? t('header.corporateAccount')
                    : t('header.personalAccount')
                }}
              </v-list-item-subtitle>
            </div>

            <template #prepend>
              <img
                :src="
                  isUsingCorporateAccount
                    ? shouldUseEmptyStateLogo
                      ? emptyStateNoIconCorporate
                      : String(selectedAccount?.logo)
                    : user?.profilePicture
                "
                alt=""
                :class="[isUsingCorporateAccount ? 'rounded' : 'rounded-xl']"
                :style="
                  isUsingCorporateAccount && shouldUseEmptyStateLogo
                    ? 'padding: 8px; background-color: var(--v-theme-green);'
                    : ''
                "
                width="32"
              />
            </template>
          </v-list-item>
        </template>

        <v-list max-height="50vh" class="overflow-y-auto">
          <AccountMenu @update:model-value="(val) => (accountMenu = val)" />
        </v-list>
      </v-list-group>

      <v-divider v-if="isMobile" style="opacity: 0.9" />

      <v-list v-if="isUsingCorporateAccount" density="compact">
        <v-list-item
          v-for="item in menus"
          :id="item.id"
          :key="item.id"
          :to="item.to"
          router
          :active="isMenuActive(item.id)"
          active-class="text-light-green"
          :class="{
            'notifications-item': item.id === 'notifications',
            'rail-item': isRailCollapsed,
          }"
        >
          <template #prepend>
            <template v-if="item.id === 'notifications'">
              <div v-if="isRailCollapsed" class="notifications-rail-wrapper">
                <v-badge
                  :model-value="(item.badge ?? 0) > 0"
                  :content="formatBadgeContent(item.badge)"
                  color="warning"
                  floating
                  :offset-x="6"
                  :offset-y="10"
                  class="rail-notification-badge"
                >
                  <v-icon
                    size="20"
                    :class="{ 'text-light-green': isMenuActive(item.id) }"
                  >
                    mdi-bell-outline
                  </v-icon>
                </v-badge>
              </div>
            </template>

            <template v-else>
              <img
                :src="isMenuActive(item.id) ? item.iconGreen : item.iconGrey"
                alt=""
                height="24"
                class="mr-4"
              />
            </template>
          </template>

          <template v-if="item.id === 'notifications'">
            <div v-if="isRailCollapsed" class="notifications-rail-filler" />

            <div v-else class="notifications-open-layout">
              <v-icon
                size="24"
                class="notifications-open-icon"
                :class="{ 'text-light-green': isMenuActive(item.id) }"
              >
                mdi-bell-outline
              </v-icon>

              <span
                class="text-subtitle-2 font-weight-medium notifications-open-text"
                :class="{ 'text-light-green': isMenuActive(item.id) }"
              >
                {{ item.text }}
              </span>

              <v-badge
                v-if="(item.badge ?? 0) > 0"
                :content="formatBadgeContent(item.badge)"
                inline
                color="warning"
                class="notifications-badge"
              />
            </div>
          </template>

          <v-list-item-title
            v-else
            class="text-subtitle-2 font-weight-medium nav-item-title"
            :class="{ 'text-light-green': isMenuActive(item.id) }"
          >
            <span>{{ item.text }}</span>
          </v-list-item-title>
        </v-list-item>

        <v-list-item
          id="corporative-account-config"
          class="d-flex flex-row"
          :to="
            companyId
              ? {
                  name: 'detail-corporative-account',
                  params: { id: companyId },
                }
              : {}
          "
          router
          :active="isSettingsActive()"
          active-class="text-light-green"
        >
          <template #prepend>
            <v-avatar size="24">
              <img
                :src="isSettingsActive() ? settingsGreen : settingsGrey"
                alt=""
                height="24"
              />
            </v-avatar>
          </template>

          <v-list-item-title
            class="text-subtitle-2 font-weight-medium"
            :class="{ 'text-light-green': isSettingsActive() }"
          >
            {{ t('sidebar.accountSettings') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-list>
  </v-navigation-drawer>

  <v-main style="height: 100vh" />
</template>

<style scoped>
.v-list {
  padding: 0 !important;
}

.v-list-item__append {
  width: 2.8rem !important;
  max-height: 45px !important;
  max-width: 45px !important;
  margin-top: 0 !important;
}

.bottom-border {
  border-bottom: 1px solid #e0e0e0 !important;
  border-radius: 0 !important;
}

.v-list-item:hover {
  background-color: rgba(141, 198, 63, 0.2) !important;
}

.v-list .v-list-item--nav:not(:only-child) {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.nav-item-title {
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
}

.notifications-badge {
  display: inline-flex;
  margin-left: 4px !important;
}

.notifications-badge :deep(.v-badge__content),
.rail-notification-badge :deep(.v-badge__content) {
  min-width: 16px !important;
  height: 16px !important;
  padding: 0 4px !important;

  border-radius: 999px !important;

  font-family: 'Open Sans', sans-serif !important;
  font-size: 8px !important;
  font-weight: 400 !important;
  line-height: 8px !important;
  text-align: center;

  color: #fff !important;

  display: flex;
  align-items: center;
  justify-content: center;
}

.notifications-rail-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-left: 0;
  transform: translateX(1px);
}

.notifications-rail-filler {
  width: 100%;
}

.notifications-open-layout {
  display: inline-flex;
  align-items: center;
}

.notifications-open-icon {
  flex: 0 0 24px;

  margin-right: 14px; /* igual ao prepend dos outros */
}

.notifications-open-text {
  display: inline-flex;
  align-items: center;
}

.notifications-badge :deep(.v-badge__badge),
.rail-notification-badge :deep(.v-badge__badge) {
  min-width: 16px !important;
  height: 16px !important;
  padding: 0 4px !important;
  border-radius: 999px !important;

  font-family: 'Open Sans', sans-serif !important;
  font-size: 8px !important;
  font-weight: 400 !important;
  line-height: 1 !important;
  text-align: center;

  color: #fff !important;

  display: flex;
  align-items: center;
  justify-content: center;
}

.rail-item :deep(.v-list-item__prepend) {
  width: 100%;
  justify-content: center;
}

.v-list-item__prepend {
  margin-inline-end: 8px !important;
}

.notifications-item:not(.rail-item) :deep(.v-list-item__content) {
  padding: 0 !important;
}

.notifications-item:not(.rail-item) :deep(.v-list-item__prepend) {
  display: none !important;
}

.rail-notification-badge {
  display: inline-flex;
}

@media (max-width: 960px) {
  body.no-scroll {
    overflow: hidden !important;
  }

  .v-list-item__append {
    max-height: 48px !important;
    max-width: 48px !important;
    margin-right: 18px;
  }
}
</style>
